import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React from 'react';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogCategoryCarousel } from '@/components/catalog-category-carousel';
import {
  CatalogCategoryType,
  ItemListOption,
} from '@/contexts/item-context-types';
import { CatalogItemCarousel } from '@/components/catalog-item-carousel';
import { CatalogStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import {
  ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX,
  ItemRecommendationCarousel,
} from '@/components/orders/item-recommendation-carousel';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import { ScrollViewProps, StyleProp, ViewStyle } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCatalogCarouselViewability } from '@/hooks/use-catalog-carousel-viewability';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';
import { VolumePromotionsCarousel } from '@/components/orders/volume-promotions-carousel';

type Navigation = StackNavigationProp<CatalogStackParams, CATALOG_ROUTES>;

interface CarouselSection {
  data: {
    category: CatalogCategoryType;
    categoryItemCount: number;
    items: ItemListOption[];
  }[];
  key: null;
}

interface Props {
  contentContainerStyle: StyleProp<ViewStyle>;
  onSelectCategory: (category: CatalogCategoryType) => void;
  scrollViewProps: ScrollViewProps;
  sections: CarouselSection[];
}

export const CategoryCarousels = ({
  contentContainerStyle,
  scrollViewProps,
  sections,
  onSelectCategory,
}: Props) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  const navigation = useNavigation<Navigation>();

  const { isGuestMode } = useUserContext();
  const { itemRecommendations } = useItemRecommendationsContext();
  const carouselViewability = useCatalogCarouselViewability();
  const newItemCardEnabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );

  return (
    <KeyboardAwareRecyclerList
      estimatedItemSize={320}
      ItemSeparatorComponent={null}
      ListHeaderComponent={
        <>
          {isGuestMode ? null : (
            <VolumePromotionsCarousel
              locationPrefix="enhanced_supplier_catalog"
              onNavigateToDetails={(promotionUUID) => {
                navigation.navigate(CATALOG_ROUTES.VolumePromotionDetail, {
                  promotionUUID,
                });
              }}
              onSeeAllPress={() => {
                Analytics.track('supplier_catalog.promotions_see_all.press');
                navigation.navigate(CATALOG_ROUTES.VolumePromotionsList);
              }}
            />
          )}
          {itemRecommendations.length > 0 ? (
            isSmallLayout ? (
              <ItemRecommendationCarousel
                items={itemRecommendations.map((ir) => ir?.metadata!.item)}
                onSeeAllPress={() => {
                  navigation.push(CATALOG_ROUTES.ItemRecommendationsList);
                }}
              />
            ) : (
              <CatalogItemCarousel
                count={itemRecommendations.length}
                listItems={itemRecommendations.map((ir) => ir?.metadata!.item)}
                newItemCardEnabled={newItemCardEnabled}
                onSeeAllPress={() => {
                  navigation.push(CATALOG_ROUTES.ItemRecommendationsList);
                }}
                title={i18n.t('based_on_history')}
                trackingSource={ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX}
              />
            )
          ) : null}
        </>
      }
      renderItem={({ item }) => {
        return (
          <CatalogCategoryCarousel
            category={item.category}
            categoryItemCount={item.categoryItemCount}
            listItems={item.items}
            onCategoryPress={() => {
              onSelectCategory(item.category);
            }}
          />
        );
      }}
      sections={sections}
      {...carouselViewability}
      {...scrollViewProps}
      contentContainerStyle={[
        contentContainerStyle,
        styles.contentContainerStyle,
        isSmallLayout ? styles.contentContainerStyleSmall : null,
      ]}
    />
  );
};

const useStyles = createStyles((theme) => ({
  contentContainerStyle: {
    paddingBottom: 8,
  },
  contentContainerStyleSmall: {
    backgroundColor: theme.colors.background,
  },
}));
